import { useEffect, useState } from "react";
import SimpleButton from "../../../../shared/components/Button/Button";
import ButtonLoader from "../../../../shared/components/ButtonLoader";
import CustomModal from "../../../../shared/components/CustomModal";
import TextBox from "../../../../shared/components/TextBox";
import NumberBox from "../../../../shared/components/NumberBox";
import {
  ACTION_LABEL,
  BUTTON_LABEL,
  icons,
  LABELS,
  toastError,
  toastSuccess,
} from "../../../../shared/constants";
import { feedbackCommentFieldValidation } from "../../../../shared/validation/customer-feedback-config-validation";
import {
  FeedbackCommentField,
  useFeedbackCommentFieldsCreateMutation,
  useFeedbackCommentFieldsDeleteMutation,
  useFeedbackCommentFieldsListQuery,
  useFeedbackCommentFieldsUpdateMutation,
} from "../../../../Features/customerFeedback/customer-feedback-slice";

// Comment Fields admin panel (contracts #11–#14). Rendered inside the
// FeedbackConfig tab wrapper, so it has no Navbar of its own.
const CommentFields = () => {
  const [fields, setFields] = useState<FeedbackCommentField[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<FeedbackCommentField | null>(null);
  const [fieldLabel, setFieldLabel] = useState("");
  const [labelHint, setLabelHint] = useState("");
  const [characterLimit, setCharacterLimit]: any = useState("");
  const [errors, setErrors]: any = useState({});
  const [deleting, setDeleting] = useState<FeedbackCommentField | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const { data, isSuccess, refetch } = useFeedbackCommentFieldsListQuery();

  const [createField, { isLoading: createLoading }] =
    useFeedbackCommentFieldsCreateMutation();
  const [updateField, { isLoading: updateLoading }] =
    useFeedbackCommentFieldsUpdateMutation();
  const [deleteField] = useFeedbackCommentFieldsDeleteMutation();

  // Helper to sort active fields first then inactive fields
  const sortAndNormalizeFields = (list: FeedbackCommentField[]) => {
    const active = list
      .filter((f) => f.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const inactive = list
      .filter((f) => !f.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const combined = [...active, ...inactive];
    return combined.map((f, idx) => ({
      ...f,
      displayOrder: idx + 1,
    }));
  };

  useEffect(() => {
    if (isSuccess && data) {
      const normalized = sortAndNormalizeFields(data.list);

      normalized.forEach((f) => {
        const orig = data.list.find(
          (item) => item.commentFieldId === f.commentFieldId,
        );
        if (orig && orig.displayOrder !== f.displayOrder) {
          updateField({
            commentFieldId: f.commentFieldId,
            displayOrder: f.displayOrder,
          });
        }
      });

      setFields(normalized);
    }
  }, [data, isSuccess]);

  const activeCount = fields.filter((f) => f.isActive).length;
  const atMaxTotal = fields.length >= 10;

  const openAdd = () => {
    if (fields.length >= 10) {
      toastError("Maximum 10 comment fields allowed");
      return;
    }
    setEditing(null);
    setFieldLabel("");
    setLabelHint("");
    setCharacterLimit("");
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (field: FeedbackCommentField) => {
    setEditing(field);
    setFieldLabel(field.fieldLabel);
    setLabelHint(field.labelHint || "");
    setCharacterLimit(field.characterLimit);
    setErrors({});
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSave = async () => {
    const { formValid, formErrors } = feedbackCommentFieldValidation({
      fieldLabel,
      characterLimit,
    });
    if (!formValid) {
      setErrors(formErrors);
      return;
    }

    if (editing) {
      const res = await updateField({
        commentFieldId: editing.commentFieldId,
        fieldLabel: fieldLabel.trim(),
        labelHint: labelHint.trim(),
        characterLimit: Number(characterLimit),
      })
        .unwrap()
        .catch((err: any) => {
          toastError(err.data?.errors?.[0]?.msg);
        });
      if (res?.status) {
        toastSuccess(res.message || "");
        refetch();
        handleClose();
      }
    } else {
      const res = await createField({
        fieldLabel: fieldLabel.trim(),
        labelHint: labelHint.trim(),
        displayOrder: fields.length + 1,
        characterLimit: Number(characterLimit),
        isActive: false,
      })
        .unwrap()
        .catch((err: any) => {
          toastError(err.data?.errors?.[0]?.msg);
        });
      if (res?.status) {
        toastSuccess(res.message || "");
        refetch();
        handleClose();
      }
    }
  };

  const handleToggleActive = async (field: FeedbackCommentField) => {
    const nextIsActive = !field.isActive;

    if (!nextIsActive && activeCount <= 1) {
      toastError(
        LABELS.MIN_ACTIVE_COMMENT_FIELDS_TEXT ||
          "At least one active comment field is required",
      );
      return;
    }

    let updatedList: FeedbackCommentField[];
    if (nextIsActive) {
      // Activating: place at the end of active comment fields
      const active = fields.filter(
        (f) => f.isActive && f.commentFieldId !== field.commentFieldId,
      );
      const inactive = fields.filter(
        (f) => !f.isActive && f.commentFieldId !== field.commentFieldId,
      );
      updatedList = [...active, { ...field, isActive: true }, ...inactive];
    } else {
      // Deactivating: place at the end of inactive comment fields
      const active = fields.filter(
        (f) => f.isActive && f.commentFieldId !== field.commentFieldId,
      );
      const inactive = fields.filter(
        (f) => !f.isActive && f.commentFieldId !== field.commentFieldId,
      );
      updatedList = [...active, ...inactive, { ...field, isActive: false }];
    }

    const reindexed = updatedList.map((f, idx) => ({
      ...f,
      displayOrder: idx + 1,
    }));
    setFields(reindexed);

    for (const f of reindexed) {
      const orig = fields.find((o) => o.commentFieldId === f.commentFieldId);
      if (
        !orig ||
        orig.isActive !== f.isActive ||
        orig.displayOrder !== f.displayOrder
      ) {
        await updateField({
          commentFieldId: f.commentFieldId,
          isActive: f.isActive,
          displayOrder: f.displayOrder,
        })
          .unwrap()
          .catch((err: any) => {
            toastError(err.data?.errors?.[0]?.msg);
          });
      }
    }
    toastSuccess("Status updated");
    refetch();
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;

    const list = [...fields];
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;

    // Re-index sequentially 1..N
    const reindexed = list.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));

    setFields(reindexed);

    // Save changes to DB for items whose displayOrder changed
    for (let i = 0; i < reindexed.length; i++) {
      if (reindexed[i].displayOrder !== fields[i]?.displayOrder) {
        await updateField({
          commentFieldId: reindexed[i].commentFieldId,
          displayOrder: reindexed[i].displayOrder,
        }).unwrap();
      }
    }
    refetch();
  };

  // Drag and drop handlers
  const handleDragStart = (
    e: React.DragEvent<HTMLTableRowElement>,
    index: number,
  ) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (
    e: React.DragEvent<HTMLTableRowElement>,
    targetIndex: number,
  ) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    const list = [...fields];
    const itemToMove = list.splice(draggedIndex, 1)[0];
    list.splice(targetIndex, 0, itemToMove);

    // Re-index sequentially 1..N
    const reindexed = list.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));

    setFields(reindexed);
    setDraggedIndex(null);

    // Save changes to DB for items whose displayOrder changed
    for (let i = 0; i < reindexed.length; i++) {
      if (reindexed[i].displayOrder !== fields[i]?.displayOrder) {
        await updateField({
          commentFieldId: reindexed[i].commentFieldId,
          displayOrder: reindexed[i].displayOrder,
        }).unwrap();
      }
    }
    refetch();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (deleting.isActive && activeCount <= 1) {
      toastError(
        LABELS.MIN_ACTIVE_COMMENT_FIELDS_TEXT ||
          "At least one active comment field is required",
      );
      setDeleting(null);
      return;
    }
    const res = await deleteField({ commentFieldId: deleting.commentFieldId })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg);
      });
    if (res?.status) {
      toastSuccess(res.message || "");
      refetch();
    }
    setDeleting(null);
  };

  return (
    <>
      <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
        <i className={icons.ICON_KEY} />
        <span>{LABELS.CONFIG_APPLIES_TO_NEW_TEXT}</span>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-muted">
            {LABELS.ACTIVE_NAME_TEXT}: {activeCount}
          </span>
        </div>
        <SimpleButton
          className="btn theme-btn theme-btn-sm btn-inverse w-auto"
          onClick={openAdd}
          disabled={atMaxTotal}
          text={LABELS.ADD_FIELD_TEXT}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-header-shadow mb-0">
          <thead>
            <tr>
              <th>{LABELS.FIELD_LABEL_TEXT}</th>
              <th>{LABELS.LABEL_HINT_TEXT}</th>
              <th>{LABELS.DISPLAY_ORDER_TEXT}</th>
              <th>{LABELS.CHARACTER_LIMIT_TEXT}</th>
              <th>{LABELS.ACTIVE_NAME_TEXT}</th>
              <th>{LABELS.ACTIONS_TEXT}</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr
                key={field.commentFieldId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={() => setDraggedIndex(null)}
                style={{
                  cursor: "grab",
                  opacity: draggedIndex === index ? 0.4 : 1,
                  backgroundColor:
                    draggedIndex === index ? "#f5efe6" : undefined,
                  transition: "background-color 0.2s, opacity 0.2s",
                }}
              >
                <td>{field.fieldLabel}</td>
                <td>{field.labelHint || "-"}</td>
                <td>{field.displayOrder}</td>
                <td>{field.characterLimit}</td>
                <td>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={field.isActive}
                      onChange={() => handleToggleActive(field)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: field.isActive ? "#3E2215" : undefined,
                        borderColor: field.isActive ? "#3E2215" : undefined,
                      }}
                      aria-label={`${field.fieldLabel} active`}
                    />
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      title={LABELS.MOVE_UP_TEXT}
                      onClick={() => handleMove(index, -1)}
                      text="↑"
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      title={LABELS.MOVE_DOWN_TEXT}
                      onClick={() => handleMove(index, 1)}
                      text="↓"
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      onClick={() => openEdit(field)}
                      text={BUTTON_LABEL.EDIT}
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      onClick={() => setDeleting(field)}
                      text={BUTTON_LABEL.DELETE}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {fields.length === 0 && (
              <tr>
                <td className="text-center" colSpan={6}>
                  <strong>{LABELS.No_DATA_FOUND_TEXT}</strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CustomModal
        show={showForm}
        close={handleClose}
        size={"md"}
        message={""}
        modalTitle={editing ? LABELS.EDIT_FIELD_TEXT : LABELS.ADD_FIELD_TEXT}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: handleClose,
            className: "btn theme-btn btn-inverse",
          },
          {
            text:
              createLoading || updateLoading ? (
                <ButtonLoader />
              ) : (
                BUTTON_LABEL.SAVE
              ),
            action: handleSave,
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      >
        <div className="form-group value-field">
          <TextBox
            name={ACTION_LABEL.FIELD_LABEL}
            placeholder={LABELS.FIELD_LABEL_TEXT}
            id={LABELS.FIELD_LABEL_TEXT}
            label={LABELS.FIELD_LABEL_TEXT}
            onChange={(e: any) => {
              setFieldLabel(e.target.value);
              setErrors({ ...errors, fieldLabel: "" });
            }}
            value={fieldLabel}
            errorMessage={errors.fieldLabel || null}
          />
        </div>
        <div className="form-group value-field">
          <TextBox
            name={ACTION_LABEL.LABEL_HINT}
            placeholder={LABELS.LABEL_HINT_TEXT}
            id={LABELS.LABEL_HINT_TEXT}
            label={LABELS.LABEL_HINT_TEXT}
            onChange={(e: any) => {
              setLabelHint(e.target.value);
            }}
            value={labelHint}
          />
        </div>
        <div className="form-group value-field">
          <NumberBox
            name={ACTION_LABEL.CHARACTER_LIMIT}
            placeholder={LABELS.CHARACTER_LIMIT_TEXT}
            id={LABELS.CHARACTER_LIMIT_TEXT}
            label={LABELS.CHARACTER_LIMIT_TEXT}
            onChange={(e: any) => {
              setCharacterLimit(e.target.value);
              setErrors({ ...errors, characterLimit: "" });
            }}
            value={characterLimit}
            errorMessage={errors.characterLimit || null}
            min={1}
          />
        </div>
      </CustomModal>

      <CustomModal
        show={!!deleting}
        close={() => setDeleting(null)}
        size={"sm"}
        message="Are you sure want to delete ?"
        modalTitle="Delete"
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setDeleting(null),
            className: "btn theme-btn btn-inverse",
          },
          {
            text: BUTTON_LABEL.CONFIRM,
            action: confirmDelete,
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      />
    </>
  );
};

export default CommentFields;
